import React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styled from 'styled-components';

import {
  productCardDefaultViews,
  productCardTextAlignments,
  productCardPillDisplayOptions,
} from 'shared/constants/customization';
import { ProductCardButtonOption } from 'types/graphql';
import { navBarColors, buttonsLinksColors } from 'shared/constants/menu-colors';
import { mediaQueries } from 'shared/styles';
import {
  DEFAULT_STRAIN_TYPE_PILL_BACKGROUND,
  getStrainTypePillBackground,
  getStrainTypePillTextColor,
  StrainTypeColorKey,
} from '@dutchie/common/utils/strain-type-colors';

import { ActiveSwitch } from 'src/components/active-switch';
import { Tooltip } from 'shared/components';
import useHasMobileApp from 'src/utils/use-has-mobile-app';
import { MenuCustomizationLayout } from '../components/menu-customization-layout';
import { OptionPicker } from '../components/option-picker';
import { ColorPickerCompact } from '../components/color-picker-compact';
import { MobileSettingsMovedBanner } from '../components/mobile-settings-moved-banner';

import { useApplyToAllLocations } from '../use-apply-to-all-locations';
import { ECOMM_APP_V2_REDESIGN_FLAG } from '../homepage/add-section-modal/app-display-type-select';
import { useProductCardsForm } from './use-product-cards-form';
import { ProductCardsFormProps } from './product-card.types';

import { Heading, HelperText, Input, Inputs, Label, SubText } from '../customize.styles';
import { ProductCardPreview } from './product-card-preview';

/** Mobile app product card color customization; only rendered/applied when this flag is enabled in LaunchDarkly. */
export const ECOMM_APP_ADVANCED_CUSTOMIZATION_FLAG = 'ecomm.app.advanced-customization';

const mobileProductCardButtonOptions = [
  { label: 'Icon', value: ProductCardButtonOption.icon },
  { label: 'Solid', value: ProductCardButtonOption.solid },
  { label: 'Border', value: ProductCardButtonOption.border },
  { label: 'Tinted', value: ProductCardButtonOption.tinted },
];

// Form field name + display label for each strain type color, alphabetized by key.
const strainTypeFields: Array<{
  key: StrainTypeColorKey;
  fieldName:
    | 'strainTypeCbd'
    | 'strainTypeCbg'
    | 'strainTypeHighCBD'
    | 'strainTypeHybrid'
    | 'strainTypeIndica'
    | 'strainTypeIndicaHybrid'
    | 'strainTypeSativa'
    | 'strainTypeSativaHybrid'
    | 'strainTypeSeed'
    | 'strainTypeThc';
  label: string;
}> = [
  { key: 'cbd', fieldName: 'strainTypeCbd', label: 'CBD' },
  { key: 'cbg', fieldName: 'strainTypeCbg', label: 'CBG' },
  { key: 'highCBD', fieldName: 'strainTypeHighCBD', label: 'High CBD' },
  { key: 'hybrid', fieldName: 'strainTypeHybrid', label: 'Hybrid' },
  { key: 'indica', fieldName: 'strainTypeIndica', label: 'Indica' },
  { key: 'indicaHybrid', fieldName: 'strainTypeIndicaHybrid', label: 'Indica-Hybrid' },
  { key: 'sativa', fieldName: 'strainTypeSativa', label: 'Sativa' },
  { key: 'sativaHybrid', fieldName: 'strainTypeSativaHybrid', label: 'Sativa-Hybrid' },
  { key: 'seed', fieldName: 'strainTypeSeed', label: 'Seed' },
  { key: 'thc', fieldName: 'strainTypeThc', label: 'THC' },
];

export function ProductCardsForm(props: ProductCardsFormProps): JSX.Element {
  const { mobileOnly = false } = props;
  const sectionName = 'Product Cards';
  const flags = useFlags();
  const hasMobileApp = useHasMobileApp();
  const showMobileAppSection = hasMobileApp && (flags[ECOMM_APP_V2_REDESIGN_FLAG] ?? false);
  const advancedCustomizationEnabled = flags[ECOMM_APP_ADVANCED_CUSTOMIZATION_FLAG] ?? false;
  const showAdvancedCustomization = showMobileAppSection && advancedCustomizationEnabled;
  // Strain type pill colors apply to the web marketplace product card, independent of the
  // mobile app section above, so they're gated on the flag alone.
  const showStrainTypeColors = advancedCustomizationEnabled;
  const {
    control,
    handlePublish,
    handleReset,
    isDirty,
    previewData,
    applyAllInput,
    afterApplyAll,
  } = useProductCardsForm(props);
  // Untyped record (rather than the hook's full FormValues) is sufficient here — this only
  // reads back the handful of strainType* fields to drive the live pill preview below.
  const watchedValues = useWatch({ control }) as Record<string, string | undefined>;
  const mobileShowStrainType = useWatch({ control, name: 'mobileShowStrainType' });
  const mobileShowPotencyDetails = useWatch({ control, name: 'mobileShowPotencyDetails' });
  const { handleApplyToLocations, hasAccessToAllLocations, chainId, totalLocationCount } = useApplyToAllLocations({
    input: applyAllInput,
    afterApplyAll,
    sectionName,
  });

  return (
    <MenuCustomizationLayout
      headerText={sectionName}
      subHeaderText='Select how product cards will appear.'
      isDirty={isDirty}
      handlePublish={() => void handlePublish()}
      handleReset={handleReset}
      previewComponent={
        <ProductCardPreview
          data={previewData}
          showMobileAppPreview={showMobileAppSection}
          showAdvancedCustomization={showAdvancedCustomization}
          showStrainTypeColors={showStrainTypeColors}
          mobileOnly={mobileOnly}
        />
      }
      handleApplyToLocations={handleApplyToLocations}
      hasAccessToAllLocations={hasAccessToAllLocations}
      chainId={chainId}
      totalLocationCount={totalLocationCount}
      hideHeader={mobileOnly}
    >
      {!mobileOnly && <MobileSettingsMovedBanner tabSegment='product-cards' />}

      {/* Default View / Display Product Weight / Full Width Images / Image Cycling / Text
          Alignment all apply to the desktop/web storefront (or both surfaces at once, e.g. Text
          Alignment) rather than being mobile-app-specific settings — hidden in mobileOnly mode. */}
      {!mobileOnly && (
        <>
          <Heading>Default View</Heading>
          <SubText>
            Select which product card type will display by default on category pages, search results and more.
          </SubText>

          <Inputs>
            <Input>
              <Controller
                name='defaultViewLarge'
                control={control}
                render={({ onChange, value }) => (
                  <OptionPicker
                    label='Large Screens'
                    options={productCardDefaultViews}
                    selectedOption={value}
                    onChange={onChange}
                  />
                )}
              />
              <HelperText>Includes desktop computers, laptops and tablets.</HelperText>
            </Input>

            <Input>
              <Controller
                name='defaultViewSmall'
                control={control}
                render={({ onChange, value }) => (
                  <OptionPicker
                    label='Small Screens'
                    options={productCardDefaultViews}
                    selectedOption={value}
                    onChange={onChange}
                  />
                )}
              />
              <HelperText>Includes mobile devices and smaller tablets.</HelperText>
            </Input>
          </Inputs>

          <Heading>Display Product Weight</Heading>
          <SubText>Display the product weight next to price on card view.</SubText>

          <Inputs>
            <Input>
              <Controller
                name='showProductWeight'
                control={control}
                render={({ onChange, value }) => (
                  <ActiveSwitch name='showProductWeight' active={value} onClick={onChange} hasLabel />
                )}
              />
            </Input>
          </Inputs>

          <Heading>Full Width Images</Heading>
          <SubText>Display product images edge-to-edge on card view.</SubText>

          <Inputs>
            <Input>
              <Controller
                name='fullWidthImages'
                control={control}
                render={({ onChange, value }) => (
                  <ActiveSwitch name='fullWidthImages' active={value} onClick={onChange} hasLabel />
                )}
              />
            </Input>
          </Inputs>

          <Heading>Image Cycling</Heading>
          <SubText>
            When enabled, product cards display only the primary image — no hover or touch image cycling.
          </SubText>

          <Inputs>
            <Input>
              <Controller
                name='disableProductCardImageCycling'
                control={control}
                render={({ onChange, value }) => (
                  <ActiveSwitch name='disableProductCardImageCycling' active={value} onClick={onChange} hasLabel />
                )}
              />
            </Input>
          </Inputs>

          <Heading>Text Alignment</Heading>
          <SubText>Select text alignment for card view</SubText>

          <Inputs>
            <Input>
              <Controller
                name='textAlignment'
                control={control}
                render={({ onChange, value }) => (
                  <OptionPicker
                    label='Text Alignment'
                    options={productCardTextAlignments}
                    selectedOption={value}
                    onChange={onChange}
                  />
                )}
              />
            </Input>
          </Inputs>
        </>
      )}

      {/* When ecomm.app.advanced-customization is on, mobile app settings live exclusively in
          the consolidated enterprise Mobile App tab (mobileOnly) — the classic
          /dispensaries/:id/customize/product-cards page shows the "They moved!" banner above
          instead of duplicating these fields. When the flag is off, the classic page still
          renders this section so the settings remain reachable. */}
      {showMobileAppSection && (mobileOnly || !advancedCustomizationEnabled) && (
        <>
          {!mobileOnly && (
            <>
              <Heading>Mobile App</Heading>
              <SubText>Select which details display on mobile app product cards.</SubText>
            </>
          )}

          {/* A single shared grid (rather than one grid instance per row) so every row's column
              widths are computed once, consistently, instead of each row's 1fr tracks being
              sized independently and drifting apart. Each "row starter" cell below is pinned to
              `gridColumn: 1` to force a fresh row regardless of how many cells preceded it (e.g.
              Strain Type Display being conditionally absent shouldn't shift Potency Details out
              of column 1) -- everything else just flows naturally into column 2 of whichever row
              its row-starter began. */}
          <VisibilityGrid>
            <Input style={{ gridColumn: 1 }} data-testid='show-strain-type-cell'>
              <Label>Show Strain Type</Label>
              <Controller
                name='mobileShowStrainType'
                control={control}
                render={({ onChange, value }) => (
                  <ActiveSwitch name='mobileShowStrainType' active={value} onClick={onChange} hasLabel />
                )}
              />
            </Input>

            {/* Only meaningful once strain type is actually shown on the card — hidden otherwise. */}
            {showAdvancedCustomization && mobileShowStrainType && (
              <Input data-testid='strain-type-display-cell'>
                <Controller
                  name='mobileStrainTypeDisplay'
                  control={control}
                  render={({ onChange, value }) => (
                    <OptionPicker
                      label='Strain Type Display'
                      options={productCardPillDisplayOptions}
                      selectedOption={value}
                      onChange={onChange}
                    />
                  )}
                />
              </Input>
            )}

            <Input style={{ gridColumn: 1 }} data-testid='show-potency-details-cell'>
              <Label>Show Potency Details</Label>
              <Controller
                name='mobileShowPotencyDetails'
                control={control}
                render={({ onChange, value }) => (
                  <ActiveSwitch name='mobileShowPotencyDetails' active={value} onClick={onChange} hasLabel />
                )}
              />
            </Input>

            {/* Only meaningful once potency details are actually shown on the card — hidden otherwise. */}
            {showAdvancedCustomization && mobileShowPotencyDetails && (
              <Input data-testid='potency-display-cell'>
                <Controller
                  name='mobilePotencyDisplay'
                  control={control}
                  render={({ onChange, value }) => (
                    <OptionPicker
                      label='Potency Display'
                      options={productCardPillDisplayOptions}
                      selectedOption={value}
                      onChange={onChange}
                    />
                  )}
                />
              </Input>
            )}

            <Input style={{ gridColumn: 1 }}>
              <Label>Show Effects</Label>
              <Controller
                name='mobileShowEffects'
                control={control}
                render={({ onChange, value }) => (
                  <ActiveSwitch name='mobileShowEffects' active={value} onClick={onChange} hasLabel />
                )}
              />
            </Input>

            <Input>
              <Label>Show Discount Names</Label>
              <Controller
                name='mobileShowDiscountNames'
                control={control}
                render={({ onChange, value }) => (
                  <ActiveSwitch name='mobileShowDiscountNames' active={value} onClick={onChange} hasLabel />
                )}
              />
            </Input>

            <Input>
              <Label>Show Collection</Label>
              <Controller
                name='mobileShowCollection'
                control={control}
                render={({ onChange, value }) => (
                  <ActiveSwitch name='mobileShowCollection' active={value} onClick={onChange} hasLabel />
                )}
              />
            </Input>

            <Input>
              <Label>Show Staff Pick</Label>
              <Controller
                name='mobileShowStaffPick'
                control={control}
                render={({ onChange, value }) => (
                  <ActiveSwitch name='mobileShowStaffPick' active={value} onClick={onChange} hasLabel />
                )}
              />
            </Input>
          </VisibilityGrid>

          <Inputs>
            <Input>
              <Controller
                name='mobileProductCardButton'
                control={control}
                render={({ onChange, value }) => (
                  <OptionPicker
                    label='Add to Cart Style'
                    options={mobileProductCardButtonOptions}
                    selectedOption={value}
                    onChange={onChange}
                  />
                )}
              />
            </Input>
          </Inputs>

          {showAdvancedCustomization && (
            <>
              <Heading>Colors</Heading>

              <ThreeColumnGrid>
                <Input>
                  <Controller
                    name='mobileBackgroundColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerCompact
                        label='Card Background'
                        colors={navBarColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#fff'}
                      />
                    )}
                  />
                </Input>

                <Input>
                  <Controller
                    name='mobileProductNameColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerCompact
                        label='Product Name'
                        colors={buttonsLinksColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#121516'}
                      />
                    )}
                  />
                </Input>

                <Input>
                  <Controller
                    name='mobileBrandColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerCompact
                        label='Brand'
                        colors={buttonsLinksColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#646d72'}
                      />
                    )}
                  />
                </Input>

                <Input>
                  <Controller
                    name='mobilePotencyColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerCompact
                        label='Potency Values'
                        colors={buttonsLinksColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#646d72'}
                      />
                    )}
                  />
                </Input>

                <Input>
                  <Controller
                    name='mobilePriceColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerCompact
                        label='Price'
                        colors={buttonsLinksColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#2c3236'}
                      />
                    )}
                  />
                </Input>

                <Input>
                  <Controller
                    name='mobileStrikethroughPriceColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerCompact
                        label='Strike-Out Price'
                        colors={buttonsLinksColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#485055'}
                      />
                    )}
                  />
                </Input>

                <Input>
                  <Controller
                    name='mobileWeightColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerCompact
                        label='Weight'
                        colors={buttonsLinksColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#646d72'}
                      />
                    )}
                  />
                </Input>
              </ThreeColumnGrid>
            </>
          )}
        </>
      )}

      {/* Strain type pill colors are used by the mobile app's strain-type pill display
          (mobileStrainTypeDisplay) as well as the web marketplace card, so — unlike the other
          sections hidden in mobileOnly mode above — this one stays visible there too; it's the
          only place to customize the colors the mobile pills actually render with. */}
      {showStrainTypeColors && (
        <>
          <HeadingRow>
            <Heading>Strain Type Colors</Heading>
            <Tooltip grey tooltipCopy='The colors selected are reflected on both web and mobile' width='220px' />
          </HeadingRow>
          <SubText>Customize the pill color shown for each strain type on product cards.</SubText>

          <PillPreviewRow>
            {strainTypeFields.map(({ key, fieldName, label }) => {
              const background = getStrainTypePillBackground(label, {
                default: watchedValues.strainTypeDefault,
                [key]: watchedValues[fieldName],
              });
              return (
                <PillPreview key={key} $background={background} $color={getStrainTypePillTextColor(background)}>
                  {label}
                </PillPreview>
              );
            })}
          </PillPreviewRow>

          <ThreeColumnGrid>
            {strainTypeFields.map(({ key, fieldName, label }) => (
              <Input key={key}>
                <Controller
                  name={fieldName}
                  control={control}
                  render={({ onChange, value }) => (
                    <ColorPickerCompact
                      label={label}
                      colors={buttonsLinksColors}
                      onSelect={onChange}
                      selectedColor={value}
                      allowCustomColorSelection
                    />
                  )}
                />
              </Input>
            ))}

            <Input key='defaultStrainType'>
              <Controller
                name='strainTypeDefault'
                control={control}
                render={({ onChange, value }) => (
                  <ColorPickerCompact
                    label='Default Strain Type'
                    colors={buttonsLinksColors}
                    onSelect={onChange}
                    selectedColor={value ?? DEFAULT_STRAIN_TYPE_PILL_BACKGROUND}
                  />
                )}
              />
            </Input>
          </ThreeColumnGrid>
        </>
      )}
    </MenuCustomizationLayout>
  );
}

// Two columns, 50% width each -- e.g. "Show Effects" and "Show Discount Names" sit side by side
// instead of each visibility toggle stacking on its own full-width row. Each usage below is its
// own instance rather than one shared grid, so pairs like "Show Strain Type"/"Strain Type
// Display" always land in the same row regardless of how many other toggles are visible.
// minmax(0, 1fr), not plain 1fr -- without the explicit 0 minimum, a grid item's default "auto"
// min-width is based on its content's min-content size, which can grow the column beyond its
// track instead of letting something like an open ColorPickerCompact popover overlay in place.
const VisibilityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 20px;
  margin-bottom: 20px;

  @media ${mediaQueries.custom(600)} {
    grid-template-columns: 1fr;
  }
`;

// Three columns for the strain type color swatches and product card colors.
const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 20px;
  row-gap: 20px;

  @media ${mediaQueries.custom(700)} {
    grid-template-columns: 1fr 1fr;
  }

  @media ${mediaQueries.custom(400)} {
    grid-template-columns: 1fr;
  }
`;

const HeadingRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 8px;

  ${Heading} {
    margin-bottom: 0;
  }
`;

const PillPreviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const PillPreview = styled.span<{ $background: string; $color: string }>`
  background: ${({ $background }) => $background};
  border-radius: 12px;
  color: ${({ $color }) => $color};
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  padding: 6px 8px;
`;
